import { Link } from '@liscio/ui';

import type { UploadCardProps } from '../../UploadCard/UploadCard';

export const SMART_TAX_ORGANIZER_CARD_CONFIG = {
  title: 'Smart Tax Organizer',
  subtitle: 'Get organizers back faster, reduce admin time',
  description:
    'Transform bulky tax organizers into personalized digital experiences. Simply upload PDF organizers from leading tax systems and get customized client questionnaires in minutes.',
  features: [
    {
      title: 'Document checklist',
      description:
        'Clients receive a personalized list of all documents they provided in the prior year',
    },
    {
      title: 'Smart questioning',
      description: 'Show/hide relevant sections and questions based on client needs',
    },
    {
      title: 'Real-time tracking',
      description: 'Monitor client progress and automate follow-ups',
    },
    {
      title: 'Fast turnaround',
      description:
        'Generate personalized organizers from prior organizers in under 5 minutes',
    },
  ],
  icon: 'folderOpen',
  iconColor: 'primary',
  uploadButtonText: 'Create Smart Organizers',
  uploadDescription:
    'Upload organizers from your tax software to generate personalized client organizers',
  instructionText: 'Drag & drop client PDF organizers here or',
} satisfies Omit<UploadCardProps, 'onDrop' | 'technicalSpecs' | 'disabled'>;

export const SMART_TAX_RETURN_CARD_CONFIG = {
  title: 'Tax Return Organizer',
  subtitle: 'Start with last year\'s return',
  description:
    'Upload a text-based Form 1040 and build a personalized digital organizer from the return data.',
  features: [
    {
      title: 'Upload one return',
      description: 'Use a standard individual Form 1040 with supporting schedules',
    },
    {
      title: 'Pre-fill what is available',
      description: 'Keep absent or uncertain values unanswered for review',
    },
    {
      title: 'Review before sending',
      description: 'Confirm the Account and Contacts before client delivery',
    },
    {
      title: 'No automatic send',
      description: 'The generated request remains under firm control',
    },
  ],
  icon: 'folderOpen',
  iconColor: 'primary',
  uploadButtonText: 'Create From Tax Return',
  uploadDescription:
    'Upload a prior-year text-based Form 1040 to generate a personalized organizer',
  instructionText: 'Drag & drop a tax return PDF here or',
} satisfies Omit<UploadCardProps, 'onDrop' | 'technicalSpecs' | 'disabled'>;

export const SMART_TAX_DELIVERY_CARD_CONFIG = {
  title: 'Smart Tax Delivery',
  subtitle: 'Complete client workflow from review to payment',
  description:
    'One integrated workflow handles return review, e-signatures, government payments, and quarterly estimated payments—all with automated progress tracking.',
  features: [
    {
      title: 'Automated signature placement',
      description: 'Federal and State forms',
    },
    {
      title: 'Step-by-step guidance',
      description: 'Clients receive a personalized walkthrough',
    },
    {
      title: 'Automated follow-up',
      description: 'Smart reminders via email',
    },
    {
      title: 'Progress visibility',
      description: 'Instant completion notifications',
    },
    {
      title: 'Estimated Payment Reminders',
      description:
        'Automatically reminds clients each quarter of payments due',
    },
  ],
  icon: 'send',
  iconColor: 'secondary',
  uploadButtonText: 'Start Client Delivery',
  uploadDescription:
    'Upload completed 1040 returns to begin client review and signing process',
  instructionText: 'Drag & drop client PDF returns here or',
} satisfies Omit<UploadCardProps, 'onDrop' | 'technicalSpecs' | 'disabled'>;

export const SmartTaxOrganizerTechnicalSpecs = () => (
  <>
    Use only standard 2025 organizers from supported tax systems. Limit 100 files
    per batch. Do not use custom or prior year organizers.{' '}
    <Link
      href="https://intercom.help/liscio-inc/en/articles/10658906-smart-tax-organizers-importing-sending"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: 'primary.main', textDecoration: 'underline' }}
    >
      See supported organizers
    </Link>
  </>
);

export const SmartTaxReturnTechnicalSpecs = () => (
  <>Use one text-based individual Form 1040 return with supporting schedules. Scans,
  locked PDFs, and packets containing conflicting returns are not supported.</>
);

export const SmartTaxDeliveryTechnicalSpecs = () => (
  <>
    Use only 2023, 2024, or 2025 client copies of 1040 returns from supported tax
    systems.{' '}
    <Link
      href="https://intercom.help/liscio-inc/en/articles/10564356-smart-tax-delivery-printing-returns"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: 'primary.main', textDecoration: 'underline' }}
    >
      See supported returns
    </Link>
  </>
);
